import axios from 'axios';
import {
  isLoggedIn,
  invalidateSession,
  getAuthType,
  AUTH_TYPE,
  getRefreshToken,
} from 'utils/auth';
import axiosInstance from 'utils/axiosInstance';
import { hasFeature } from 'utils/featureFlags';
import Store from 'store';
import * as api from './constants';
import { getProfile as getProfileApi } from './profile';
// TODO:
// Make interceptor request for any request with `withCredentials: true` config

export function login(email, password, phone) {
  const payload = phone
    ? { session: { password, phone_number: phone } }
    : { session: { email, password } };
  return axios.post(api.LOGIN_URL, payload, { withCredentials: true });
}

export function referral(ref) {
  return axios.get(api.REFERRAL_URL, { params: { ref } }).catch(error => {
    if (error.response) {
      const obj = new Error(error.response.data.error);
      obj.status = error.response.status;
      return Promise.reject(obj);
    }
    return Promise.reject(new Error(error.message));
  });
}

export function resendVerificationEmail(data) {
  return axios.post(api.RESEND_VERIFICATION_EMAIL_URL, data).catch(error => {
    if (error.response) {
      // The request was made, but the server responded with a status code
      // that falls out of the range of 2xx
      const eobj = new Error(error.response.data.message);
      eobj.status = error.response.data.status;
      return Promise.reject(eobj);
    }
    // Something happened in setting up the request that triggered an Error
    return Promise.reject(new Error(error.message));
  });
}

export function outlets() {
  if (!loggedIn()) return Promise.reject(new Error('Not logged in'));

  return axios.get(api.OUTLETS_URL, { withCredentials: true }).catch(error => {
    const { response } = error;
    return Promise.reject(response);
  });
}

export function loggedIn() {
  const hasValidSession = isLoggedIn();

  if (!hasValidSession) {
    invalidateSession();
  }
  return hasValidSession;
}

export function logout() {
  if (!loggedIn()) {
    return Promise.resolve();
  }

  let promise;
  const authType = getAuthType();
  const refreshToken = getRefreshToken();

  if (authType === AUTH_TYPE.GO_AUTH && refreshToken) {
    promise = axiosInstance.delete(
      `${api.GOID_SERVICE}/gobiz/goauth/oauth2/token`,
      {
        headers: {
          'Authentication-Type': 'go-id',
        },
        data: {
          token_type_hint: 'refresh_token',
          token: refreshToken,
        },
      }
    );
  } else if (hasFeature('enable-jwt-login-flow')) {
    promise = axiosInstance.post('/mkService/account/v2/logout', null, {
      headers: {
        Authorization: Store.get('moka:refreshToken'),
      },
    });
  } else {
    promise = axiosInstance.post('/mkService/account/v1/logout');
  }

  return promise.finally(() => {
    invalidateSession();
  });
}

export function registered(email) {
  return axios.get(
    api.REGISTER_URL + email,
    { email },
    { withCredentials: true }
  );
}

export function validatePhone(phoneNumber) {
  return axios.post(
    api.VALIDATE_PHONE,
    { phone_number: phoneNumber },
    { withCredentials: true }
  );
}

export function setBusinessInformation(id, data) {
  return axios.put(api.BUSINESS_INFO_URL + id, data, { withCredentials: true });
}

export function setBusinessInformationOnBusinessService(data) {
  return axios.post(api.BUSINESS_INFO_UPDATE_SERVICE_URL, data);
}

export function generateOtp(phoneNumber) {
  return axios
    .post(
      api.GENERATE_OTP,
      { phone_number: phoneNumber },
      { withCredentials: false }
    )
    .catch(error => {
      return Promise.reject(new Error(error.response.data.reason));
    });
}

export function validateUser(email, phoneNumber) {
  return axios.all([registered(email), validatePhone(phoneNumber)]).then(
    axios.spread(function(verifiedEmail, verifiedPhone) {
      return Promise.resolve({
        verifiedEmail: verifiedEmail.data,
        verifiedPhone: verifiedPhone.data,
      });
    })
  );
}

export function verifyOtpRequest(payload) {
  return axios.post(api.REGISTER_BUSINESS_URL, payload, {
    withCredentials: true,
  });
}

export function registerBusiness(data) {
  return axios.post(api.REGISTER_BUSINESS_URL, data).catch(error => {
    return Promise.reject(error.response.data);
  });
}

export function getBusinessTypes() {
  const url = hasFeature('business-type-new-api')
    ? api.BUSINESS_TYPES_URL_FROM_SERVICE
    : api.BUSINESS_TYPES_URL;
  return axios.get(url);
}

export function getBusinessOpeningChoices() {
  return axios.get(api.BUSINESS_OPENINGS_CHOICES_URL);
}

export function getBusinessInfo() {
  return axios.get(api.BUSINESS_INFO_UPDATE_SERVICE_URL);
}

export function selectOutlet(outletIds) {
  return axios
    .patch(
      api.UPDATE_SELECTED_OUTLET,
      { id: outletIds },
      { withCredentials: true }
    )
    .catch(error => {
      const { response } = error;
      return Promise.reject(response);
    });
}

export function getProfile() {
  return getProfileApi().catch(error => {
    const { response } = error;
    return Promise.reject(response);
  });
}

export function getBusinessCategories(businessTypeId) {
  return axios.get(api.BUSINESS_CATEGORIES_URL, {
    params: {
      business_type_id: businessTypeId,
    },
  });
}

export function getAllBusinessCategories() {
  return axios.get(api.BUSINESS_CATEGORIES_ALL);
}

export function getBusinessCategoriesAll() {
  const url = hasFeature('business-type-new-api')
    ? api.BUSINESS_CATEGORIES_ALL_URL_FROM_SERVICE
    : api.BUSINESS_CATEGORIES_ALL_URL;
  return axios.get(url);
}

export function getBusinessLocations() {
  return axios.get(api.BUSINESS_PROVINCE_URL);
}

export function getBusinessProvince() {
  return axios.get(api.BUSINESS_PROVINCE_URL);
}

export function getBusinessCity(provinceId) {
  return axios.get(`${api.BUSINESS_CITY_URL + provinceId}/cities`);
}

export function getBusinessZipcode(kecamatanId) {
  return axios.get(`${api.BUSINESS_ZIPCODE_URL + kecamatanId}/zipcode`);
}

export function getBusinessKecamatan(cityId) {
  return axios.get(`${api.BUSINESS_KECAMATAN_URL + cityId}/kecamatan`);
}

export function getBankList() {
  return axios.get(api.BANK_LIST_URL);
}

export function getResetPassword(query) {
  return axios
    .get(api.GET_RESET_PASSWORD_URL, {
      params: query,
      withCredentials: true,
    })
    .catch(error => {
      const { response } = error;
      return Promise.reject(response);
    });
}
export function resetPassword(query) {
  return axios
    .post(
      api.RESET_PASSWORD_URL,
      {
        access_token: query.access_token,
        password: query.newPassword,
        password_confirmation: query.confPassword,
      },
      { withCredentials: true }
    )
    .catch(error => {
      const { response } = error;
      return Promise.reject(response);
    });
}

export function loadPrepareEncryption() {
  return axios
    .get(api.LOAD_PREPARE_ENCRYPTION_URL, { withCredentials: true })
    .catch(error => {
      const { response } = error;
      return Promise.reject(response);
    });
}

export function getActiveBusinessApplication(userId) {
  return axios
    .get(api.GET_ACTIVE_BUSINESS_APP(userId), { withCredentials: true })
    .catch(error => {
      const { response } = error;
      return Promise.reject(response);
    });
}

export function generateOtpV2(phoneNumber) {
  return axios
    .post(api.GENERATE_OTP_V2_URL, { phone_number: phoneNumber })
    .catch(error => {
      return Promise.reject(error.response.data);
    });
}

export function verifyOtpV2(requestId, code) {
  return axios
    .post(
      api.VERIFY_OTP_URL,
      {
        request_id: requestId,
        code,
      },
      { withCredentials: true }
    )
    .catch(error => {
      return Promise.reject(error.response.data);
    });
}
